import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { listContractTemplates, saveContractTemplate, deleteContractTemplate } from "@/lib/contracts.functions";
import { CONTRACT_PLACEHOLDERS, STARTER_TEMPLATE, type ContractTemplate } from "@/lib/contracts";

export const Route = createFileRoute("/_authenticated/admin/contracts/templates")({
  head: () => ({ meta: [{ title: "Contract templates | Admin" }] }),
  component: TemplatesPage,
});

function TemplatesPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listContractTemplates);
  const saveFn = useServerFn(saveContractTemplate);
  const delFn = useServerFn(deleteContractTemplate);
  const { data = [] } = useQuery({ queryKey: ["contract-templates"], queryFn: () => listFn() as Promise<ContractTemplate[]> });
  const [edit, setEdit] = useState<{ id?: string; name: string; body: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!edit) return; setBusy(true);
    try { await saveFn({ data: edit }); toast.success("Template saved"); setEdit(null); qc.invalidateQueries({ queryKey: ["contract-templates"] }); }
    catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h1 className="text-2xl font-bold">Contract templates</h1><p className="text-sm text-muted-foreground">Save your standard contract once, then reuse it for every new employee.</p></div>
        <Button onClick={() => setEdit({ name: "Standard employment contract", body: STARTER_TEMPLATE })}>New template</Button>
      </div>

      {edit && (
        <Card>
          <CardHeader><CardTitle>{edit.id ? "Edit template" : "New template"}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1"><Label>Template name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
            <div className="space-y-1">
              <Label>Contract wording</Label>
              <Textarea className="min-h-[420px] font-mono text-sm" value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
              <p className="text-xs text-muted-foreground">These words are filled in automatically for each employee: {CONTRACT_PLACEHOLDERS.map((p) => `{{${p.key}}}`).join(", ")}</p>
            </div>
            <div className="flex gap-2"><Button onClick={save} disabled={busy}>Save template</Button><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button></div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {data.length === 0 && !edit && <p className="text-sm text-muted-foreground">No templates yet. Click "New template" to start with a basic outline you can edit.</p>}
        {data.map((t) => (
          <Card key={t.id}><CardContent className="flex flex-wrap items-center justify-between gap-2 p-4">
            <div><p className="font-semibold">{t.name}</p><p className="text-xs text-muted-foreground">Updated {new Date(t.updated_at).toLocaleDateString("en-ZA")}</p></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEdit({ id: t.id, name: t.name, body: t.body })}>Edit</Button>
              <Button size="sm" variant="outline" onClick={async () => { if (!confirm("Delete this template? Existing contracts are not affected.")) return; try { await delFn({ data: { id: t.id } }); qc.invalidateQueries({ queryKey: ["contract-templates"] }); } catch (e) { toast.error((e as Error).message); } }}>Delete</Button>
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}
